'use client';

import { MovieCatalogRequest, MovieCatalogResponse } from '@/types/movie-catalog';
import { GetMoviesRequest } from '@/types/paged-requests/movie-paged-request';
import { commonFetch, CommonFetchResponse } from '@/utils/common-fetch';
import { buildUrlWithParams } from '@/utils/url-params';
import { TheaterMovieIds } from '@cinemark/boxoffice-api-client';
import { Movie, MovieGlobal, PagedListOfMovie } from '@cinemark/content-api-client';

class MoviesApiService {
  private readonly moviesUrl = '/papi/movies';
  private readonly errorMessage = 'Movie was not found';

  async getMovieGlobal(signal?: AbortSignal, depth?: number): Promise<CommonFetchResponse<MovieGlobal>> {
    const url = depth === undefined ? `${this.moviesUrl}/global` : buildUrlWithParams(`${this.moviesUrl}/global`, { depth });
    return await commonFetch<MovieGlobal>(url, signal ? { signal } : undefined);
  }

  async getMovie(payloadId: string, signal?: AbortSignal, depth?: number): Promise<CommonFetchResponse<Movie>> {
    const url = depth === undefined ? `${this.moviesUrl}/${payloadId}` : buildUrlWithParams(`${this.moviesUrl}/${payloadId}`, { depth });
    return await commonFetch<Movie>(url, signal ? { signal } : undefined);
  }

  async getMovieCatalog(request: MovieCatalogRequest, signal?: AbortSignal): Promise<CommonFetchResponse<MovieCatalogResponse>> {
    const searchParams = new URLSearchParams({
      category: request.category,
      sort: request.filters.sort,
    });

    if (request.businessDate) searchParams.set('businessDate', request.businessDate.toISOString());
    if (request.location.latitude !== undefined) searchParams.set('latitude', String(request.location.latitude));
    if (request.location.longitude !== undefined) searchParams.set('longitude', String(request.location.longitude));
    if (request.location.preserveCategoryMembership) searchParams.set('preserveCategoryMembership', 'true');
    if (request.location.zipCode) searchParams.set('zipCode', request.location.zipCode);

    request.filters.formats.forEach(format => searchParams.append('formats', format));
    request.filters.genres.forEach(genre => searchParams.append('genres', genre));
    request.filters.languages.forEach(language => searchParams.append('languages', language));
    request.filters.ratings.forEach(rating => searchParams.append('ratings', rating));

    return await commonFetch<MovieCatalogResponse>(`${this.moviesUrl}/catalog?${searchParams.toString()}`, signal ? { signal } : undefined);
  }

  async getMovies(pagedRequest: GetMoviesRequest, signal?: AbortSignal): Promise<PagedListOfMovie> {
    const response = await commonFetch<PagedListOfMovie>(
      buildUrlWithParams(this.moviesUrl, pagedRequest as Record<string, unknown>), signal ? { signal } : undefined);
    return response?.data;
  }

  async getMovieIdsByTheater(theaterId: number, signal?: AbortSignal): Promise<TheaterMovieIds> {
    const response = await commonFetch<TheaterMovieIds>(`${this.moviesUrl}/by-theater/${theaterId}`, signal ? { signal } : undefined);
    return response?.data;
  }

  async getMovieByCinemarkMovieId(cinemarkMovieId: number, signal?: AbortSignal): Promise<Movie> {
    const response = await commonFetch<PagedListOfMovie>(buildUrlWithParams(this.moviesUrl, { cinemarkMovieIds: cinemarkMovieId }),
      signal ? { signal } : undefined);
    const movies: PagedListOfMovie = response?.data;
    if (movies?.data?.length) {
      return movies.data[0];
    }

    throw new Error(this.errorMessage);
  }

  async getMovieBySlug(slug: string, signal?: AbortSignal): Promise<Movie> {
    const response = await this.getMovies({ pageSize: 1, slug }, signal);
    if (response?.data?.length) {
      return response.data[0];
    }

    throw new Error(this.errorMessage);
  }
}

export const moviesApiService = new MoviesApiService();
